// web/src/scene/rtls/usePlaybackPositions.ts
//
// Returns interpolated positions for every asset at the current playback
// timestamp. In live mode (or when an asset has no trail history), returns
// the cached live position from rtls.assets[id].

import { useMemo } from 'react'
import { useAppSelector } from '../../app/useAppSelector'
import type { RTLSPosition, PositionHistoryEntry } from '@shared/types/rtlsTypes'

/** Binary search — largest index with timestamp <= target. Returns -1 if none. */
function findFloor(entries: PositionHistoryEntry[], target: number): number {
  let lo = 0
  let hi = entries.length - 1
  let result = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (entries[mid].timestamp <= target) {
      result = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return result
}

function lerp(a: RTLSPosition, b: RTLSPosition, t: number): RTLSPosition {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  }
}

export function usePlaybackPositions(): ReadonlyMap<string, RTLSPosition> {
  const playbackMode      = useAppSelector(s => s.rtls.playbackMode)
  const playbackTimestamp = useAppSelector(s => s.rtls.playbackTimestamp)
  const positionHistory   = useAppSelector(s => s.rtls.positionHistory)
  const assetsRecord      = useAppSelector(s => s.rtls.assets)

  // Stable empty-Map sentinel for live mode. Consumers fall back to
  // `asset.position` via the `?? asset.position` coalesce, so returning an
  // empty Map is equivalent to the per-asset override in live mode while
  // keeping the reference stable across positionHistory ticks. This
  // prevents downstream memos (RTLSOverlay.snappedAssets etc.) from
  // invalidating on every live polling tick.
  return useMemo(() => {
    if (playbackMode !== 'playback' || playbackTimestamp == null) {
      return EMPTY_POSITIONS
    }

    const result = new Map<string, RTLSPosition>()
    for (const [id, asset] of Object.entries(assetsRecord)) {
      const entries = positionHistory[id] as PositionHistoryEntry[] | undefined
      if (!entries || entries.length === 0) {
        result.set(id, asset.position)
        continue
      }

      const floorIdx = findFloor(entries, playbackTimestamp)
      if (floorIdx < 0) {
        // Before any recorded history — use the oldest known point.
        result.set(id, entries[0].position)
      } else if (floorIdx === entries.length - 1) {
        // At or past the most recent history entry.
        result.set(id, entries[floorIdx].position)
      } else {
        // Interpolate between floorIdx and floorIdx+1.
        const a = entries[floorIdx]
        const b = entries[floorIdx + 1]
        const span = b.timestamp - a.timestamp
        const t = span > 0 ? (playbackTimestamp - a.timestamp) / span : 0
        result.set(id, lerp(a.position, b.position, Math.min(1, Math.max(0, t))))
      }
    }

    return result
  }, [playbackMode, playbackTimestamp, positionHistory, assetsRecord])
}

const EMPTY_POSITIONS: ReadonlyMap<string, RTLSPosition> = new Map()
